import axios from 'axios';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { get, map } from 'lodash';
import { IAssemblyPublicationData } from 'src/features/locations/listAssemblyPublicationPage/modules/AssemblyPublicationTable/AssemblyPublicationTable.interface';
import { STATUS_LIST } from 'src/features/locations/listPublicationPage/PublicationPage.interface';
import {
  ApiResponse,
  IAssembly,
  IComponent,
  IPublication,
} from 'src/interfaces';
import { errorMessages } from 'src/utils';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

const getLocationStatus = (str: string | undefined) => {
  let status = '';
  if (str === 'UP_TO_DATE' || str === 'UP_TO_DATE_OFFLINE') {
    status = STATUS_LIST.UP_TO_DATE;
  } else if (str === 'UPDATE_IN_PROGRESS') {
    status = STATUS_LIST.UPDATE_IN_PROGRESS;
  } else if (str === 'OUT_OF_DATE') {
    status = STATUS_LIST.OUT_OF_DATE;
  } else if (str === 'FAILED') {
    status = STATUS_LIST.FAILED;
  }

  return status;
};

export const mapLocationsWithHQ = (response: ApiResponse) => {
  const formattedData = map(response.data, (item: IPublication) => {
    return {
      nsn: item.hq.store_number,
      locationName: item.hq.name,
      status: getLocationStatus(item.hq.publication_status),
      timeZone: item.hq.timezone,
      publicationDate: item.hq.requested_publication_stamp
        ? dayjs(item.hq.requested_publication_stamp).format(
            'MM-DD-YYYY HH:mm:ss',
          )
        : '',
      modified_at: item.modified_at
        ? dayjs(item.modified_at).format('MM-DD-YYYY HH:mm:ss')
        : '',
      locationReported: item.hq.location_reported
        ? dayjs(item.hq.location_reported).format('Do MMM YYYY HH:mm:ss')
        : '',
      systemStatus: item.hq.location_status ?? '',
    };
  });

  return {
    data: formattedData,
    totalCount: response.totalCount,
    totalPages: response.totalPages,
  };
};

export const mapPublicationWithIncludes = (
  response: ApiResponse,
  includes: string[],
) => {
  const fromComponent = includes.includes('fromComponent')
    ? (get(response, 'includes.fromComponent.data', []) as IComponent[])
    : [];
  const toComponent = includes.includes('toComponent')
    ? (get(response, 'includes.toComponent.data', []) as IComponent[])
    : [];
  const assemblies = includes.includes('assemblies')
    ? (get(response, 'includes.assemblies.data', []) as IAssembly[])
    : [];

  const formattedData: IAssemblyPublicationData[] = map(
    response.data,
    (item) => {
      return {
        display_name: item.display_name,
        id: item.id,
        numberOfAssemblies: item.assembly_id.length,
        fromComponent: fromComponent.find(
          (comp) => comp.id === item.from_component_id,
        )?.name,
        fromComponentId: fromComponent.find(
          (comp) => comp.id === item.from_component_id,
        )?.id,
        toComponent: toComponent.find(
          (comp) => comp.id === item.to_component_id,
        )?.name,
        toComponentId: toComponent.find(
          (comp) => comp.id === item.to_component_id,
        )?.id,
        assemblies: assemblies.filter((asm) =>
          item.assembly_id.includes(asm.id),
        ),
        assemblyIds: item.assembly_id,
        hasLocations: item.hasLocations ?? false,
        user: item.user,
        userRole: item.role,
        modified_at: item.modified_at
          ? dayjs(item.modified_at).format('MM-DD-YYYY HH:mm:ss')
          : '',
      };
    },
  );

  return {
    data: formattedData,
    totalCount: response.totalCount,
    totalPages: response.totalPages,
  };
};

const extractErrorCode = (errorString: string): string => {
  return errorString.replace(/^Error:\s*/, '').trim();
};

export const getCustomErrorMessage = (
  error: unknown,
  props?: Record<string, any>,
): string => {
  if (axios.isAxiosError(error)) {
    const errorResponse = error.response?.data;

    if (typeof errorResponse?.error === 'string') {
      const errorCode = extractErrorCode(errorResponse.error);
      const messageTemplate = errorMessages[errorCode];
      if (typeof messageTemplate === 'function') {
        return messageTemplate(props ?? errorResponse.details ?? {});
      }

      return messageTemplate;
    }
  }

  return 'An unknown error occurred. Please try again.';
};
